import { type FormEvent, useEffect, useId, useRef, useState } from "react";

import {
  PASSWORD_RULE_TEXT,
  getPatientPolicyHelpText,
  meetsPolicy,
} from "../../lib/auth/passwordPolicy";

import { errorMessageOf, isSessionExpired } from "./patientAuthErrors";
import { changeInitialPassword, issueTokens } from "./patientAuthenticationApi";
import { INITIAL_PASSWORD_SET_PREFIX } from "./patientAuthMessages";
import {
  Actions,
  BlockButton,
  ErrorMessage,
  Field,
  Heading,
} from "./patientAuthenticationParts";

type Props = {
  oneTimeToken: string;
  onSignedIn: () => void;
  onSessionExpired: (message: string) => void;
};

type FieldErrors = { newPassword?: string; confirmation?: string };

export function PatientInitialPasswordForm({
  oneTimeToken,
  onSignedIn,
  onSessionExpired,
}: Props) {
  const id = useId();
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState("");
  const [errorTick, setErrorTick] = useState(0);
  const [loading, setLoading] = useState(false);
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const confirmationRef = useRef<HTMLInputElement>(null);

  // エラーを出したら、先頭のエラーのある欄へフォーカスを移す
  useEffect(() => {
    if (errorTick === 0) return;
    if (error || fieldErrors.newPassword) newPasswordRef.current?.focus();
    else if (fieldErrors.confirmation) confirmationRef.current?.focus();
  }, [errorTick, error, fieldErrors.newPassword, fieldErrors.confirmation]);

  const filled = newPassword !== "" && confirmation !== "";

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!filled || loading) return;
    const errors: FieldErrors = {};
    if (!meetsPolicy(newPassword)) {
      errors.newPassword = `パスワードは、${PASSWORD_RULE_TEXT}`;
    }
    if (newPassword !== confirmation) {
      errors.confirmation = "確認用パスワードが一致しません。";
    }
    setFieldErrors(errors);
    setError("");
    if (errors.newPassword || errors.confirmation) {
      setErrorTick((tick) => tick + 1);
      return;
    }
    setLoading(true);
    let issuable: string;
    try {
      issuable = await changeInitialPassword(oneTimeToken, newPassword);
    } catch (e) {
      if (isSessionExpired(e)) {
        onSessionExpired(errorMessageOf(e));
        return;
      }
      setError(errorMessageOf(e));
      setErrorTick((tick) => tick + 1);
      setLoading(false);
      return;
    }
    try {
      await issueTokens(issuable);
    } catch (e) {
      // パスワードはもう変わり、この段のワンタイムトークンも発行のワンタイムトークンも使い直せないので、
      // どの失敗でも最初の段へ戻し、新しいパスワードでログインし直してもらう
      onSessionExpired(`${INITIAL_PASSWORD_SET_PREFIX}${errorMessageOf(e)}`);
      return;
    }
    // 画面を移す間は押せないままにする
    onSignedIn();
  };

  const errorId = `${id}-error`;

  return (
    <form onSubmit={submit} noValidate>
      <Heading focusOnMount>新しいパスワードの設定</Heading>
      {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
      <p className="mb-4 text-sm">
        仮パスワードでログインしました。続けて、これから使うパスワードを決めてください。
      </p>
      <Field
        id={`${id}-new-password`}
        ref={newPasswordRef}
        label="新しいパスワード"
        password
        autoComplete="new-password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        disabled={loading}
        help={getPatientPolicyHelpText("initial")}
        error={fieldErrors.newPassword}
        extraDescribedBy={error ? errorId : undefined}
      />
      <Field
        id={`${id}-confirmation`}
        ref={confirmationRef}
        label="確認用パスワード"
        password
        autoComplete="new-password"
        value={confirmation}
        onChange={(e) => setConfirmation(e.target.value)}
        disabled={loading}
        error={fieldErrors.confirmation}
        help={
          filled
            ? undefined
            : "新しいパスワードと確認用を入力すると「設定してログイン」を押せます。"
        }
      />
      <Actions>
        <BlockButton type="submit" disabled={!filled || loading}>
          {loading ? "送信中…" : "設定してログイン"}
        </BlockButton>
      </Actions>
    </form>
  );
}
